import { useEffect, useMemo, useState } from "react";
import Card from "components/card";
import Loader from "components/loader/Loader";
import { LazyLoadImage } from "react-lazy-load-image-component";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import PaginationControls from "components/paginationControls/PaginationControls";
import { Box, Icon, Text, IconButton, Tooltip, Link, FormControl, FormLabel, Input } from "@chakra-ui/react";
import { FiInbox } from "react-icons/fi";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { FaEye } from "react-icons/fa6";
import TooltipComponent from "components/tooltip";
import { MdBlock, MdCancel } from "react-icons/md";
import moment from "moment";
import 'moment/locale/es';
import { currencyFormat } from "utils/Funciones";
import InputField from "components/fields/InputField";

const columnHelper = createColumnHelper();

function TableCartItemsDetalles({
    tableData,
    pageCount = 1,
}) {

    const [sorting, setSorting] = useState([]);

    const columns = useMemo(() => [
        columnHelper.accessor("product.imagen", {
            header: () => <span className="font-bold">Imagen</span>,
            cell: info => (
                <LazyLoadImage
                    alt="Sin imagen"
                    src={`/images/productos/${info.getValue()}`}
                    effect="blur"
                    className="w-12 h-12 object-cover rounded-lg"
                />
            ),
            enableSorting: false
        }),
        columnHelper.accessor("product.name", {
            header: () => <span className="font-bold">Nombre</span>,
            cell: info => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor("product.codigointerno", {
            header: () => <span className="font-bold">Código interno</span>,
            cell: info => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor("quantity", {
            header: () => <span className="font-bold">Cantidad</span>,
            cell: info => <span>{info.getValue()}</span>,
        }),
        columnHelper.accessor("price_unit", {
            header: () => <span className="font-bold">Precio unitario</span>,
            cell: info => <span className="font-semibold">{currencyFormat(info.getValue())}</span>,
        }),
        columnHelper.accessor("product", {
            header: () => <span className="font-bold">Total</span>,
            cell: info => {
                const { quantity = 0, price_unit = 0 } = info.row.original;
                const total = quantity * price_unit;
                return <span className="font-semibold">{currencyFormat(total)}</span>;
            },
        }),
    ], []);

    const table = useReactTable({
        data: tableData?.cartItems,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true,
        pageCount,
        debugTable: false,
    });

    return (
        <Card extra="w-full h-full sm:overflow-x-auto !rounded-none">
            <div>
                <Box className="w-full overflow-x-auto rounded-md">
                    <table className="min-w-full text-left text-sm">
                        <thead className="bg-gray-300/50 dark:bg-navy-600/50">
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map(header => (
                                        <th
                                            key={header.id}
                                            onClick={header.column.getToggleSortingHandler()}
                                            className={`p-2 cursor-pointer font-semibold ${header.column.columnDef.meta?.headerClass || ''}`}
                                        >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map(row => (
                                    <tr key={row.id} className="border-t border-gray-300/50 dark:border-navy-600/50 hover:bg-gray-300/30 
                  dark:hover:bg-navy-600/30 transition-colors duration-500 ease-in-out">
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id} className={`p-2 ${cell.column.columnDef.meta?.headerClass || ''}`}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={table.getAllColumns().length}>
                                        <Box
                                            py={10}
                                            textAlign="center"
                                            className="text-center flex flex-col items-center justify-center text-gray-500 dark:text-gray-400"
                                        >
                                            <Icon as={FiInbox} boxSize={10} className="mb-2" />
                                            <Text fontSize="md" fontWeight="medium">
                                                No se encontraron resultados
                                            </Text>
                                        </Box>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Box>
            </div>
            <div className="mt-5 flex flex-wrap gap-4">
                <InputField
                    required={false}
                    disabled={true}
                    variant="input"
                    extra="w-full md:w-3/12 mb-3 p-2"
                    label="Método de pago"
                    placeholder="Método de pago"
                    id="metodopago"
                    type="text"
                    name="metodopago"
                    value={
                        (
                            [
                                { code: '01', name: 'en efectivo' },
                                { code: '02', name: 'yape' },
                                { code: '03', name: 'plin' },
                                { code: '04', name: 'bim' },
                                { code: '05', name: 'depósito en cuenta' },
                                { code: '06', name: 'tarjeta de débito' },
                                { code: '07', name: 'tarjeta de crédito' },
                                { code: '08', name: 'transferencia bancaria' },
                                { code: '09', name: 'giro' }
                            ].find(m => m.code === tableData.metodopago)?.name || ''
                        )
                    }
                />
            </div>
            <div className="mt-8 w-full flex flex-col items-end space-y-3 md:space-y-4">
                <div className="w-full max-w-sm p-4">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-2 border-b border-gray-200 dark:border-white/10 pb-2">
                        Resumen de Compra
                    </h2>

                    <div className="flex justify-between text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <span>Ope. Gravada</span>
                        <span>
                            {tableData.cartItems?.length > 0
                                ? currencyFormat(tableData.total / 1.18)
                                : currencyFormat(0)}
                        </span>
                    </div>

                    <div className="flex justify-between text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        <span>IGV (18%)</span>
                        <span>
                            {tableData.cartItems?.length > 0
                                ? currencyFormat(tableData.total - tableData.total / 1.18)
                                : currencyFormat(0)}
                        </span>
                    </div>

                    <div className="flex justify-between text-base font-semibold text-gray-900 dark:text-white mt-3 pt-2 border-t border-gray-200 dark:border-white/10">
                        <span>Total</span>
                        <span>{currencyFormat(tableData.totalFinal)}</span>
                    </div>
                </div>
            </div>

        </Card >
    )
}

export default TableCartItemsDetalles